import React, { useState, useEffect, useRef } from "react";
import styles from "./locality-modal.module.css";
import Input from "../../ui/input";
import { MyIcons } from "../../icons/my-icons";
import Button from "../../ui/button";
import Table from "../../ui/table";
import { getLocalities, createLocality, getLocalityById, editLocality } from "../../../services/apiService";

const LocalityModal = ({ isOpen, onClose }) => {
  const modalRef = useRef(null);
  const firstInputRef = useRef(null);
  const [formData, setFormData] = useState({
    postalCode: "",
    municipality: "",
  });
  const [localities, setLocalities] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingLocalityId, setEditingLocalityId] = useState(null);

  const fetchLocalities = async () => {
    try {
      const localitiesData = await getLocalities();
      setLocalities(localitiesData);
    } catch (error) {
      console.error("Error fetching localities:", error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchLocalities();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async () => {
    try {
      const localityData = {
        codigoPostal: formData.postalCode,
        municipio: formData.municipality,
      };

      if (isEditMode) {
        await editLocality(editingLocalityId, localityData);
      } else {
        await createLocality(localityData);
      }
      
      // Refrescar la lista de localidades
      await fetchLocalities();
      
      // Limpiar el formulario
      setFormData({
        postalCode: "",
        municipality: "",
      });
      setIsEditMode(false);
      setEditingLocalityId(null);
      
    } catch (error) {
      console.error("Error al guardar localidad:", error);
    }
  };

  const handleRowClick = async (locality) => {
    try {
      const localityDetails = await getLocalityById(locality.id);
      setFormData({
        postalCode: localityDetails.codigoPostal,
        municipality: localityDetails.municipio,
      });
      setIsEditMode(true);
      setEditingLocalityId(locality.id);
      
      // Hacer scroll al inicio del modal y focus en el primer input
      setTimeout(() => {
        if (modalRef.current) {
          modalRef.current.scrollTop = 0;
        }
        if (firstInputRef.current) {
          firstInputRef.current.focus();
        }
      }, 100);
    } catch (error) {
      console.error("Error fetching locality details:", error);
    }
  };

  const handleCancelEdit = () => {
    setFormData({
      postalCode: "",
      municipality: "",
    });
    setIsEditMode(false);
    setEditingLocalityId(null);
  };

  const headers = [
    { label: "Código Postal", key: "codigoPostal" },
    { label: "Municipio", key: "municipio" },
  ];

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContainer} ref={modalRef}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>Localidades</h3>
          <button className={styles.closeButton} onClick={onClose}>
            <MyIcons
              name="close"
              width={24}
              height={24}
              color="black"
            />
          </button>
        </div>

        <div className={styles.formSection}>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "16px",
              width: "100%",
            }}
          >
            <Input
              label="Código Postal"
              name="postalCode"
              value={formData.postalCode}
              onChange={handleInputChange}
              placeholder="08001"
              style={{ flex: 1 }}
            />
            <Input
              ref={firstInputRef}
              label="Municipio"
              name="municipality"
              value={formData.municipality}
              onChange={handleInputChange}
              placeholder="Barcelona"
              style={{ flex: 1 }}
            />
          </div>
        </div>

        <div style={{ marginBottom: "16px", display: "flex", gap: "12px" }}>
          <Button 
            type="button" 
            variant="primary" 
            onClick={handleSubmit}
            disabled={!formData.postalCode.trim() || !formData.municipality.trim()}
          >
            {isEditMode ? "GUARDAR CAMBIOS" : "AÑADIR LOCALIDAD"}
          </Button>
          {isEditMode && (
            <Button type="button" variant="secondary" onClick={handleCancelEdit}>
              CANCELAR
            </Button>
          )}
        </div>

        <div className={styles.tableSection}>
          <Table 
            headers={headers} 
            data={localities}
            onPress={handleRowClick}
          />
        </div>
      </div>
    </div>
  );
};

export default LocalityModal;
